// FILE: src/theme/index.js
// One MUI theme with light and dark colour schemes (plan WP-8, matrix 10.2,
// 10.3). CSS variables + the `data-mui-color-scheme` attribute on <html>
// switch schemes without re-rendering; public/index.html sets the attribute
// before the app loads, so there is no flash of the wrong theme.
//
// Accessibility rules baked in here (MTO 5.1–5.4, WCAG 2.0 AA):
// - Inter (self-hosted), sans-serif, nothing smaller than 1rem (= 12 pt);
// - sizes in rem, so browser zoom and the text-size setting scale everything;
// - links always underlined; focus always visible; 44 px touch targets;
// - colour never the only signal (status chips carry an icon and words).
import { alpha, createTheme } from "@mui/material/styles";
import { tokens, FONT_STACK } from "./tokens";

export const COLOR_SCHEME_ATTRIBUTE = "data-mui-color-scheme";
export const MODE_STORAGE_KEY = "mui-mode";

function palette(t) {
  return {
    primary: { main: t.primary, dark: t.primaryHover, light: t.primaryTint, contrastText: t.onPrimary },
    secondary: { main: t.info, contrastText: t.onPrimary },
    success: { main: t.primary, dark: t.primaryHover, light: t.primaryTint, contrastText: t.onPrimary },
    error: { main: t.error, light: t.errorTint, dark: t.error, contrastText: t.onPrimary },
    warning: { main: t.warning, light: t.warningTint, dark: t.warning, contrastText: t.onPrimary },
    info: { main: t.info, light: t.infoTint, dark: t.info, contrastText: t.onPrimary },
    text: { primary: t.text, secondary: t.textSecondary, disabled: alpha(t.text, 0.55) },
    background: { default: t.page, paper: t.surface },
    divider: t.divider,
    action: { selected: t.primaryTint, hover: alpha(t.primary, 0.06), focus: alpha(t.primary, 0.12) },
    // App-specific tokens, reachable as theme.vars.palette.zun.*
    zun: {
      primaryTint: t.primaryTint,
      inputBorder: t.inputBorder,
      surfaceRaised: t.surfaceRaised,
      errorTint: t.errorTint,
      warningTint: t.warningTint,
      infoTint: t.infoTint,
      focus: t.focus,
      googleAttribution: t.googleAttribution
    }
  };
}

const rem = (px) => `${px / 16}rem`;

const typography = {
  fontFamily: FONT_STACK,
  htmlFontSize: 16,
  fontSize: 16,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: { fontSize: rem(30), fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.02em" },
  h2: { fontSize: rem(24), fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.015em" },
  h3: { fontSize: rem(20), fontWeight: 650, lineHeight: 1.3, letterSpacing: "-0.01em" },
  h4: { fontSize: rem(18), fontWeight: 650, lineHeight: 1.35 },
  h5: { fontSize: rem(17), fontWeight: 650, lineHeight: 1.4 },
  h6: { fontSize: rem(16), fontWeight: 650, lineHeight: 1.4 },
  subtitle1: { fontSize: rem(17), fontWeight: 600, lineHeight: 1.45 },
  subtitle2: { fontSize: rem(16), fontWeight: 600, lineHeight: 1.45 },
  body1: { fontSize: rem(16), lineHeight: 1.6 },
  body2: { fontSize: rem(16), lineHeight: 1.55 },
  button: { fontSize: rem(16), fontWeight: 600, textTransform: "none", letterSpacing: 0 },
  caption: { fontSize: rem(16), lineHeight: 1.5 },
  overline: { fontSize: rem(16), fontWeight: 600, lineHeight: 1.5, letterSpacing: "0.02em", textTransform: "none" }
};

const theme = createTheme({
  cssVariables: { colorSchemeSelector: COLOR_SCHEME_ATTRIBUTE },
  colorSchemes: {
    light: { palette: palette(tokens.light) },
    dark: { palette: palette(tokens.dark) }
  },
  typography,
  shape: { borderRadius: 10 },
  spacing: 8,
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        html: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
        body: { backgroundColor: theme.vars.palette.background.default },
        "::selection": { backgroundColor: theme.vars.palette.zun.primaryTint, color: theme.vars.palette.text.primary },
        // WCAG 2.4.7: a focus ring everyone can see, in both themes.
        ":focus-visible": {
          outline: `3px solid ${theme.vars.palette.zun.focus}`,
          outlineOffset: 2
        },
        // MTO 3.3–3.4: no distracting motion; respect the device setting.
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
            scrollBehavior: "auto !important"
          }
        },
        "html[data-reduce-motion='true'] *, html[data-reduce-motion='true'] *::before, html[data-reduce-motion='true'] *::after": {
          animationDuration: "0.01ms !important",
          animationIterationCount: "1 !important",
          transitionDuration: "0.01ms !important",
          scrollBehavior: "auto !important"
        },
        "a:not([class])": { color: theme.vars.palette.primary.main, textUnderlineOffset: "0.18em" }
      })
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { backgroundImage: "none" } }
    },
    MuiCard: {
      defaultProps: { variant: "outlined" },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 14,
          borderColor: theme.vars.palette.divider,
          backgroundColor: theme.vars.palette.background.paper
        })
      }
    },
    MuiCardHeader: {
      defaultProps: { slotProps: { title: { variant: "h3", component: "h2" } } },
      styleOverrides: { root: { padding: "20px 24px 0" } }
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 24, "&:last-child": { paddingBottom: 24 } } }
    },
    MuiButtonBase: { defaultProps: { disableRipple: false } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, minHeight: 44, paddingInline: 18, fontWeight: 600 },
        sizeSmall: { minHeight: 36, paddingInline: 12 },
        sizeLarge: { minHeight: 52, paddingInline: 24, fontSize: rem(17) },
        outlined: ({ theme }) => ({ borderColor: theme.vars.palette.zun.inputBorder })
      }
    },
    MuiIconButton: {
      styleOverrides: { root: { width: 44, height: 44, borderRadius: 10 } }
    },
    MuiLink: {
      defaultProps: { underline: "always" },
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: 500,
          textUnderlineOffset: "0.18em",
          textDecorationThickness: "1px",
          textDecorationColor: "currentColor",
          "&:hover": { textDecorationThickness: "2px" },
          color: theme.vars.palette.primary.main
        })
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          backgroundColor: theme.vars.palette.background.paper,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.vars.palette.zun.inputBorder },
          "& .MuiOutlinedInput-notchedOutline legend": { fontSize: "1em" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.vars.palette.text.primary },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: 2, borderColor: theme.vars.palette.primary.main }
        }),
        input: { paddingTop: 13, paddingBottom: 13 }
      }
    },
    // MUI shrinks a floating label to 75 % (12 px). MTO 5.1 wants text
    // ≥ 12 pt (16 px), so labels stay full size on the border and the
    // notch in the outline is cut for full-size text.
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.vars.palette.text.secondary,
          "&.MuiInputLabel-outlined.MuiInputLabel-shrink": {
            transform: "translate(13px, -12px) scale(1)",
            maxWidth: "calc(100% - 26px)"
          },
          "&.MuiInputLabel-outlined.MuiInputLabel-shrink.MuiInputLabel-sizeSmall": {
            transform: "translate(13px, -12px) scale(1)"
          }
        })
      }
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: rem(16), marginInline: 2, marginTop: 6 } }
    },
    MuiTextField: { defaultProps: { variant: "outlined" } },
    MuiSelect: { defaultProps: { variant: "outlined" } },
    MuiCheckbox: { styleOverrides: { root: { padding: 10 } } },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          color: theme.vars.palette.text.primary,
          borderBottom: `1px solid ${theme.vars.palette.divider}`
        })
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
          borderRight: `1px solid ${theme.vars.palette.divider}`
        })
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 10,
          marginInline: 8,
          marginBlock: 2,
          minHeight: 44,
          "&.Mui-selected": {
            backgroundColor: theme.vars.palette.zun.primaryTint,
            color: theme.vars.palette.primary.main,
            fontWeight: 650,
            boxShadow: `inset 3px 0 0 ${theme.vars.palette.primary.main}`
          },
          "&.Mui-selected:hover": { backgroundColor: theme.vars.palette.zun.primaryTint }
        })
      }
    },
    MuiListItemIcon: { styleOverrides: { root: { minWidth: 40, color: "inherit" } } },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({ borderBottomColor: theme.vars.palette.divider, fontSize: rem(16), paddingBlock: 14 }),
        head: ({ theme }) => ({
          fontWeight: 650,
          color: theme.vars.palette.text.secondary,
          backgroundColor: theme.vars.palette.background.default,
          whiteSpace: "nowrap"
        })
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({ "&.MuiTableRow-hover:hover": { backgroundColor: theme.vars.palette.action.hover } })
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: rem(16), borderRadius: 8, height: "auto", minHeight: 30 },
        label: { paddingBlock: 3 },
        icon: { fontSize: rem(18) }
      }
    },
    MuiAlert: {
      defaultProps: { variant: "standard" },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          fontSize: rem(16),
          alignItems: "flex-start",
          border: "1px solid transparent",
          "& .MuiAlert-message": { paddingBlock: 9 },
          "&.MuiAlert-standardSuccess": {
            backgroundColor: theme.vars.palette.zun.primaryTint,
            color: theme.vars.palette.text.primary,
            borderColor: theme.vars.palette.divider,
            "& .MuiAlert-icon": { color: theme.vars.palette.primary.main }
          },
          "&.MuiAlert-standardInfo": {
            backgroundColor: theme.vars.palette.zun.infoTint,
            color: theme.vars.palette.text.primary,
            "& .MuiAlert-icon": { color: theme.vars.palette.info.main }
          },
          "&.MuiAlert-standardWarning": {
            backgroundColor: theme.vars.palette.zun.warningTint,
            color: theme.vars.palette.text.primary,
            "& .MuiAlert-icon": { color: theme.vars.palette.warning.main }
          },
          "&.MuiAlert-standardError": {
            backgroundColor: theme.vars.palette.zun.errorTint,
            color: theme.vars.palette.text.primary,
            "& .MuiAlert-icon": { color: theme.vars.palette.error.main }
          }
        }),
        icon: { paddingBlock: 9 }
      }
    },
    MuiAlertTitle: { styleOverrides: { root: { fontWeight: 650, fontSize: rem(16), marginBottom: 2 } } },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({ height: 8, borderRadius: 999, backgroundColor: theme.vars.palette.divider }),
        bar: { borderRadius: 999 }
      }
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16, backgroundImage: "none" } }
    },
    MuiDialogTitle: {
      defaultProps: { variant: "h3", component: "h2" },
      styleOverrides: { root: { paddingTop: 22 } }
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { fontSize: rem(16), fontWeight: 500, padding: "8px 12px", borderRadius: 8 } }
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: "none", fontWeight: 600, fontSize: rem(16), minHeight: 48 } }
    },
    MuiSkeleton: { defaultProps: { animation: "wave" } },
    MuiDivider: { styleOverrides: { root: ({ theme }) => ({ borderColor: theme.vars.palette.divider }) } },
    MuiMenuItem: { styleOverrides: { root: { minHeight: 44, fontSize: rem(16) } } },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: "none",
          fontWeight: 600,
          fontSize: rem(16),
          borderColor: theme.vars.palette.zun.inputBorder,
          "&.Mui-selected": {
            backgroundColor: theme.vars.palette.zun.primaryTint,
            color: theme.vars.palette.primary.main
          }
        })
      }
    },
    MuiBreadcrumbs: { styleOverrides: { root: { fontSize: rem(16) } } },
    MuiPaginationItem: { styleOverrides: { root: { fontSize: rem(16), minWidth: 40, height: 40 } } },
    MuiTablePagination: {
      styleOverrides: {
        root: { fontSize: rem(16) },
        selectLabel: { fontSize: rem(16) },
        displayedRows: { fontSize: rem(16) }
      }
    }
  }
});

export default theme;
